import { useCallback, useState } from 'react';

import { HIGH_ACCURACY_ENABLED, MAXIMUM_AGE_MS, TIMEOUT_MS } from './constants';
import type { CurrentLocationCoordinates, UseCurrentLocationReturn } from './types';

const useCurrentLocation = (): UseCurrentLocationReturn => {
    const [isLoading, setIsLoading] = useState(false);

    const fetchCurrentLocation = useCallback((): Promise<CurrentLocationCoordinates | null> => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            return Promise.resolve(null);
        }

        setIsLoading(true);

        return new Promise<CurrentLocationCoordinates | null>(resolve => {
            navigator.geolocation.getCurrentPosition(
                position => {
                    setIsLoading(false);

                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                error => {
                    // Silent fail — the caller treats a null result as
                    // "permission denied / timeout / unavailable" and keeps
                    // the UI usable. Still surface the underlying reason
                    // for debugging.
                    console.error('Current location lookup failed', error);
                    setIsLoading(false);
                    resolve(null);
                },
                {
                    enableHighAccuracy: HIGH_ACCURACY_ENABLED,
                    maximumAge: MAXIMUM_AGE_MS,
                    timeout: TIMEOUT_MS
                }
            );
        });
    }, []);

    return { fetchCurrentLocation, isLoading };
};

export default useCurrentLocation;
